'use client';

import React, { useCallback, useEffect, useMemo, useState, useTransition } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import { mediaSrc } from '@/contexts/media/utils/mediaSrc';
import { Trash2, Loader2, ImagePlus, Play, Calendar, Image as ImageIcon, Shapes } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/components/ui/alert-dialog';
import { cn } from '@/shared/utils/cn';
import type { SavedMaze } from '@/contexts/games/types';
import { listSavedMazes } from '@/contexts/games/actions/listSavedMazes';
import { deleteSavedMaze } from '@/contexts/games/actions/deleteSavedMaze';

interface SavedMazesGalleryProps {
  onMazeSelect: (maze: SavedMaze) => void;
  className?: string;
}

/** Visual identity per maze kind (DB stores `image-maze` / `template-maze`). */
function mazeKind(maze: SavedMaze): { emoji: string; Icon: typeof ImageIcon; labelKey: string } {
  const t = maze.mazeType as string;
  if (t.startsWith('image')) return { emoji: '🖼️', Icon: ImageIcon, labelKey: 'games.maze.modeImage' };
  if (t.startsWith('template')) return { emoji: '🔷', Icon: Shapes, labelKey: 'games.maze.modeTemplate' };
  if (t.startsWith('radial')) return { emoji: '🎯', Icon: Shapes, labelKey: 'games.maze.modeRadial' };
  return { emoji: '🏁', Icon: Shapes, labelKey: 'games.maze.modeTemplate' };
}

export function SavedMazesGallery({ onMazeSelect, className }: SavedMazesGalleryProps) {
  const t = useTranslations();
  const locale = useLocale();
  const [mazes, setMazes] = useState<SavedMaze[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [isPending, startTransition] = useTransition();

  const dateFmt = useMemo(
    () => new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short', year: 'numeric' }),
    [locale],
  );

  const fetchMazes = useCallback(async (pageNum: number) => {
    setIsLoading(true);
    try {
      const result = await listSavedMazes({ page: pageNum, pageSize: 12 });
      setMazes(result.mazes);
      setTotal(result.total);
      setHasMore(result.hasMore);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMazes(page);
  }, [page, fetchMazes]);

  const handleDeleteConfirm = useCallback(() => {
    if (!deleteConfirmId) return;
    const mazeId = deleteConfirmId;
    setDeleteConfirmId(null);
    setDeletingId(mazeId);
    startTransition(async () => {
      const result = await deleteSavedMaze(mazeId);
      setDeletingId(null);
      if (result.success) await fetchMazes(page);
    });
  }, [deleteConfirmId, fetchMazes, page]);

  // ── Loading (first paint) ────────────────────────────────────────
  if (isLoading && mazes.length === 0) {
    return (
      <div className={cn('flex items-center justify-center py-12', className)}>
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // ── Empty state ──────────────────────────────────────────────────
  if (mazes.length === 0) {
    return (
      <div
        className={cn(
          'flex flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-border/70 bg-muted/20 px-6 py-10 text-center',
          className,
        )}
        data-testid="saved-mazes-empty"
      >
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted text-2xl">
          🗺️
        </div>
        <p className="text-sm font-medium text-foreground">{t('games.maze.savedMazesEmpty')}</p>
        <p className="text-xs text-muted-foreground">{t('games.maze.savedMazesEmptyHint')}</p>
      </div>
    );
  }

  // ── Gallery grid ─────────────────────────────────────────────────
  return (
    <div className={cn('flex flex-col gap-3', className)} data-testid="saved-mazes-gallery">
      <div className="flex items-center justify-between">
        <h3 className="flex items-center gap-1.5 text-sm font-semibold text-foreground">
          <ImagePlus className="h-4 w-4 text-primary" />
          {t('games.maze.savedMazesTitle')}
        </h3>
        <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
          {total} {t('games.maze.savedMazesCount')}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        {mazes.map((maze) => {
          const isDeleting = deletingId === maze.id;
          const kind = mazeKind(maze);
          const thumb = maze.thumbnailBlobUrl || maze.sourceImageBlobUrl;
          const created = maze.createdAt ? dateFmt.format(new Date(maze.createdAt)) : null;
          return (
            <div
              key={maze.id}
              role="button"
              tabIndex={0}
              onClick={() => !isDeleting && onMazeSelect(maze)}
              onKeyDown={(e) => {
                if ((e.key === 'Enter' || e.key === ' ') && !isDeleting) {
                  e.preventDefault();
                  onMazeSelect(maze);
                }
              }}
              className={cn(
                'group relative flex flex-col overflow-hidden rounded-xl border border-border bg-card text-left shadow-xs transition-all',
                'cursor-pointer hover:-translate-y-0.5 hover:border-primary/50 hover:shadow-md',
                'focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
                isDeleting && 'pointer-events-none opacity-50',
              )}
              data-testid={`saved-maze-${maze.id}`}
            >
              {/* Thumbnail */}
              <div className="relative aspect-square w-full overflow-hidden bg-muted">
                {thumb ? (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img
                    src={mediaSrc(thumb)}
                    alt={t(kind.labelKey)}
                    className="h-full w-full object-cover transition-transform duration-200 group-hover:scale-105"
                    loading="lazy"
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center text-4xl">
                    {kind.emoji}
                  </div>
                )}

                {/* Mode badge */}
                <span className="absolute left-1.5 top-1.5 inline-flex items-center gap-1 rounded-full bg-background/85 px-2 py-0.5 text-[10px] font-medium text-foreground shadow-xs backdrop-blur-sm">
                  <kind.Icon className="h-3 w-3" />
                  {t(kind.labelKey)}
                </span>

                {/* Play affordance on hover */}
                <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/0 opacity-0 transition-opacity group-hover:bg-black/20 group-hover:opacity-100">
                  <span className="inline-flex items-center gap-1 rounded-full bg-primary px-3 py-1 text-xs font-medium text-primary-foreground shadow-sm">
                    <Play className="h-3 w-3 fill-current" />
                    {t('games.maze.openMaze')}
                  </span>
                </div>

                {/* Delete button */}
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setDeleteConfirmId(maze.id);
                  }}
                  disabled={isDeleting || isPending}
                  className={cn(
                    'absolute right-1.5 top-1.5 flex h-7 w-7 items-center justify-center rounded-full bg-background/85 text-destructive shadow-xs backdrop-blur-sm transition',
                    'opacity-0 hover:bg-destructive hover:text-destructive-foreground group-hover:opacity-100 focus-visible:opacity-100',
                  )}
                  aria-label={t('games.maze.deleteSavedMaze')}
                  data-testid={`delete-maze-${maze.id}`}
                >
                  {isDeleting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                </button>
              </div>

              {/* Meta row */}
              <div className="flex items-center justify-between gap-1 px-2 py-1.5">
                <span className="truncate text-xs font-medium capitalize text-foreground">
                  {maze.difficulty}
                </span>
                {created && (
                  <span className="inline-flex shrink-0 items-center gap-1 text-[10px] text-muted-foreground">
                    <Calendar className="h-3 w-3" />
                    {created}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {hasMore && (
        <div className="flex justify-center">
          <Button variant="outline" size="sm" onClick={() => setPage((p) => p + 1)} disabled={isLoading}>
            {isLoading ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
            {t('games.maze.loadMore')}
          </Button>
        </div>
      )}

      <AlertDialog open={!!deleteConfirmId} onOpenChange={(open) => { if (!open) setDeleteConfirmId(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('games.maze.deleteSavedMaze')}</AlertDialogTitle>
            <AlertDialogDescription>{t('games.maze.deleteConfirmDesc')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('games.maze.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteConfirm}>
              {t('games.maze.deleteSavedMaze')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
